import {
    IDisplaySlide,
    MinimumSlideInformation,
    NodeType,
    Slide,
    SlideDetails,
    SlideType,
    SlideTypeCategory
} from "@pcp/core/interfaces";
import { isNotNullOrUndefined } from "./null-checks";

// Slides having thumb_n|h and image variants (low|med|high)
export const imageSlideTypes = [
    SlideType.lesson,
    SlideType.chapter,
    SlideType.graphic,
    SlideType.graphicboard,
    SlideType.textboard,
    SlideType.picture,
    SlideType.perceptionImage,
    SlideType.driverscamChapter,
    SlideType.maxChapter,
    SlideType.bookpage,
    SlideType.scrollVideo,
];

// Modules with one level of nesting only:
// "lection slide" = Type.K && NodeType.L

export const isLectionWithoutChapter = (slide: Pick<Slide, 'type' | 'nodeType'>): boolean =>
    isChapterSlideType(slide.type) && slide.nodeType === NodeType.lection;

export const isLectionSlide = (slide: Pick<Slide, 'type' | 'nodeType'>): boolean =>
    isLectionSlideType(slide.type) || isLectionWithoutChapter(slide);

export const isChapterSlide = (slide: Pick<Slide, 'type' | 'nodeType'>): boolean =>
    isChapterSlideType(slide.type) && slide.nodeType !== NodeType.lection;

export const isLectionSlideType = (type: SlideType): boolean =>
    [SlideType.lesson].includes(type);

export const isChapterSlideType = (type: SlideType): boolean =>
    [SlideType.chapter, SlideType.driverscamChapter, SlideType.maxChapter].includes(type);

export const isVideoSlideType = (type: SlideType): boolean =>
    [SlideType.video, SlideType.driverscam].includes(type);

export const isDynamicallyCreatedSlideType = (type: SlideType): boolean =>
    [SlideType.whiteboard, SlideType.textboard_generated].includes(type);

export const isDynamicExtensionSlideType = (type: SlideType): boolean =>
    [SlideType.text, SlideType.graphic].includes(type);

export const isDynamicExtensionSlide = (slide: Pick<Slide, 'type'>): boolean =>
    [SlideType.text, SlideType.graphic].includes(slide.type);

export const isStaticExtensionSlide = (slide: Pick<Slide, 'type'>): boolean => !isDynamicExtensionSlide(slide);

export const extractSlideTypeCategory = (slide: undefined | null | SlideDetails | IDisplaySlide): SlideTypeCategory => {
    if (isNotNullOrUndefined(slide)) {
        if (slide.type === SlideType.empty) {
            return 'none';
        }

        if ([SlideType.video, SlideType.driverscam].includes(slide.type) && !!slide.vimeoVideoId) {
            return 'video';
        }

        if (slide.type === SlideType.interactive) {
            return 'interactive';
        }

        if (slide.type === SlideType.rubber) {
            return 'rubber';
        }

        if (slide.type === SlideType.difficultQuestion) {
            return 'difficult-question';
        }

        if (slide.type === SlideType.voting || slide.type === SlideType.blendedLearning) {
            return 'voting';
        }

        // must stay above the 'canvas' catch-all, or a VDO slide renders as a plain image
        if (slide.type === SlideType.vdo) {
            return 'vdo';
        }

        if ([SlideType.interactive, SlideType.rubber, SlideType.video].includes(slide.type) === false) {
            // Everything that is not video/interactive/rubber renders on the fabric canvas.
            return 'canvas';
        }
    }

    return 'none';
}

export const isImageSlideType = (slide: Pick<IDisplaySlide | SlideDetails | Slide, 'type'>): boolean =>
    imageSlideTypes.includes(slide.type);

/** Slide is a dynamic text extension element of a text board. */
export const isTextExtensionType = (type: SlideType): boolean => type === SlideType.text;

/** Slide is a dynamic graphic extension element of a grahpic board. */
export const isGraphicExtensionType = (type: SlideType): boolean => type === SlideType.graphic;

/** Slide is a dynamic extension extension element of any text board type */
export const isDynamicExtensionType = (type: SlideType): boolean => isTextExtensionType(type) || isGraphicExtensionType(type);

export const isTextBoard = (slide: MinimumSlideInformation): boolean => isTextBoardType(slide.type);
export const isGraphicBoard = (slide: MinimumSlideInformation): boolean => isGraphicBoardType(slide.type);
export const isAnyBoard = (slide: MinimumSlideInformation): boolean => isTextBoard(slide) || isGraphicBoard(slide);

export const isTextBoardType = (type: SlideType): boolean =>
    type === SlideType.textboard || type === SlideType.textboard_generated;

export const isGraphicBoardType = (type: SlideType): boolean =>
    type === SlideType.graphicboard;

/**
 * Checks if the slide is a CustomerSlide.
 * NOTE: It's currently not possible to distinguish between CustomerSlides and CustomerModuleSlides
 *       on the client side. Thus their checks are the same. Needs to be changed if we need an exact distinction.
 */
export const isCustomerSlide = (slide: Slide): boolean => isCustomerSlideId(slide.id);
export const isCustomerSlideId = (slideId: number): boolean => slideId >= 500000 && slideId < 600000;

export const isCustomerModuleSlide = (slideId: number): boolean => slideId >= 500000 && slideId < 600000;

/**
 * a silde is as bookmark not usable if
 * - activatedSlideDetails is undefined, null or empty
 * - activatedSlideDetails.id < 1
 */
export const isBookmarkable = (slide: undefined | MinimumSlideInformation): boolean =>
    slide !== undefined
    && Number.isFinite(slide.id)
    && Number.isInteger(slide.id)
    && slide.id >= 1;
